import { motion, AnimatePresence } from "framer-motion";
import { navigate } from "../hooks/useRoute";

export interface NavSubItem {
  title: string;
  desc: string;
  href: string;
  badge?: string;
  icon?: string;
}

export interface NavOffcanvasData {
  category: string;
  overviewHref: string;
  overviewLabel: string;
  items: NavSubItem[];
  featured?: {
    badge: string;
    title: string;
    desc: string;
    href: string;
    ctaText: string;
  };
}

export const navOffcanvasMap: Record<string, NavOffcanvasData> = {
  "/services": {
    category: "Engineering Practices & Solutions",
    overviewHref: "/services",
    overviewLabel: "All Services Overview →",
    items: [
      {
        title: "IT & ITeS Software",
        desc: "Custom applications, high-throughput backend APIs, and systems integration.",
        href: "/services#it-ites",
        badge: "Core Practice",
        icon: "💻",
      },
      {
        title: "SaaS & Digital Platforms",
        desc: "RaBuOS architecture, dynamic headless CMS, and cloud tenant platforms.",
        href: "/services#saas-platforms",
        badge: "RaBuOS Q4 Beta",
        icon: "☁️",
      },
      {
        title: "Applied R&D & Prototyping",
        desc: "Custom PCB layouts, ESP32/ARM firmware, and wireless sensor nodes.",
        href: "/services#rnd-hardware",
        badge: "In-House Lab",
        icon: "⚡",
      },
      {
        title: "Robotics & Industrial IoT",
        desc: "Control logic, microcontrollers, SCADA/PLC interfacing, and automation.",
        href: "/services#robotics-automation",
        badge: "Industry 4.0",
        icon: "🤖",
      },
      {
        title: "Security & IP Surveillance",
        desc: "Smart IP camera networks, biometric access, and perimeter security.",
        href: "/services#security-systems",
        badge: "Infrastructure",
        icon: "🛡️",
      },
      {
        title: "Technology Training",
        desc: "Corporate developer upskilling and university engineering workshops.",
        href: "/services#training-education",
        badge: "Education",
        icon: "🎓",
      },
    ],
    featured: {
      badge: "Commercial Pricing",
      title: "Transparent Fixed Pricing Schedules",
      desc: "Review fixed-tier delivery budgets starting from ₹7,999 up to full-scale SaaS platforms.",
      href: "/pricing",
      ctaText: "View Pricing Schedules →",
    },
  },

  "/courses": {
    category: "Technology Syllabi & Training Tracks",
    overviewHref: "/courses",
    overviewLabel: "Browse All Courses →",
    items: [
      {
        title: "Python Full-Stack & Automation",
        desc: "Master Python programming, automated scripting, web scraping, and REST APIs.",
        href: "/courses#python-fullstack",
        badge: "Popular",
        icon: "🐍",
      },
      {
        title: "Full-Stack Web Engineering",
        desc: "Modern React 18, Next.js, Node.js backend, PostgreSQL, and Docker containerization.",
        href: "/courses#mern-stack",
        badge: "Full-Stack",
        icon: "⚛️",
      },
      {
        title: "Embedded Systems & IoT",
        desc: "C/C++ microcontroller programming, ESP32, FreeRTOS, and MQTT telemetry.",
        href: "/courses#embedded-iot",
        badge: "Hardware",
        icon: "🔌",
      },
      {
        title: "Data Science & Machine Learning",
        desc: "NumPy, Pandas, Exploratory Data Analysis, and Scikit-learn predictive models.",
        href: "/courses#python-data-science",
        badge: "AI Track",
        icon: "📊",
      },
      {
        title: "Cyber Security & Ethical Hacking",
        desc: "Network security, OWASP Top 10 vulnerability assessment, and CERT-In compliance.",
        href: "/courses#cyber-security",
        badge: "Security",
        icon: "🔒",
      },
      {
        title: "Software Testing & QA",
        desc: "Automated web testing with Playwright/Selenium, API verification, and CI/CD.",
        href: "/courses#software-testing",
        badge: "QA Track",
        icon: "🧪",
      },
    ],
    featured: {
      badge: "Custom Syllabi",
      title: "Need an Unlisted Subject?",
      desc: "We formulate tailored engineering curricula and institutional training programs.",
      href: "/contact",
      ctaText: "Inquire Custom Topic →",
    },
  },

  "/blog": {
    category: "Technical Publications & Architecture Deep Dives",
    overviewHref: "/blog",
    overviewLabel: "All Technical Articles →",
    items: [
      {
        title: "Architecting RaBuOS with Bun",
        desc: "Multi-tenant enterprise OS on Bun & TypeScript with sub-millisecond dispatch.",
        href: "/blog/architecting-rabuos-multi-tenant-enterprise-os-bun-typescript",
        badge: "Architecture",
        icon: "⚡",
      },
      {
        title: "Industrial IoT & LoRaWAN",
        desc: "Real-time 10km wireless sensor telemetry from ESP32 edge to cloud gateways.",
        href: "/blog/industrial-iot-lorawan-sensor-telemetry-edge-to-cloud",
        badge: "Hardware & IoT",
        icon: "📡",
      },
      {
        title: "DPDP Act 2023 Compliance",
        desc: "Technical implementation guide for zero-trust data protection and consent logs.",
        href: "/blog/dpdp-act-2023-technical-compliance-zero-trust-architecture",
        badge: "Data Privacy",
        icon: "🛡️",
      },
      {
        title: "Modern 32-bit Embedded Systems",
        desc: "Why modern electronics are transitioning from 8-bit to ESP32 & ARM Cortex.",
        href: "/blog/modern-embedded-systems-transitioning-8bit-to-esp32-arm-cortex",
        badge: "Firmware",
        icon: "🔬",
      },
    ],
    featured: {
      badge: "Newsfeed",
      title: "Live Platform Dispatches",
      desc: "Check official press releases, firmware dispatches, and statutory announcements.",
      href: "/news",
      ctaText: "Read Company News →",
    },
  },

  "/about": {
    category: "Corporate Structure & Governance",
    overviewHref: "/about",
    overviewLabel: "Company Overview & Principles →",
    items: [
      {
        title: "About RADIECE",
        desc: "Founding engineering principles, delivery mindset, and registered office details.",
        href: "/about#who-we-are",
        badge: "Company",
        icon: "🏛️",
      },
      {
        title: "Careers & Open Positions",
        desc: "Apply for remote UI/UX Designer & Developer (1–2 yrs exp, Figma & Frontend).",
        href: "/careers",
        badge: "Hiring",
        icon: "💼",
      },
      {
        title: "Security Center & Bug Bounty",
        desc: "Submit vulnerability findings, review 24h triage SLAs, and CERT-In policies.",
        href: "/security",
        badge: "Security",
        icon: "🔐",
      },
      {
        title: "Annual Maintenance Contracts (AMC)",
        desc: "Structured software & website maintenance tiers, backups, and SLA hours.",
        href: "/amc",
        badge: "SLA Support",
        icon: "📋",
      },
      {
        title: "Statutory Credentials",
        desc: "CIN: U58200KL2026PTC104862 • GSTIN: 32AAQCR1162B1ZR • Kakkanad, Ernakulam.",
        href: "/about#statutory",
        badge: "Statutory",
        icon: "📄",
      },
      {
        title: "Legal & Data Protection",
        desc: "Master Terms of Service, Privacy Policy (DPDP Act 2023), and Data Retention.",
        href: "/privacy",
        badge: "Trust",
        icon: "⚖️",
      },
    ],
    featured: {
      badge: "Inquiry Desk",
      title: "Direct Engineering Contact",
      desc: "Speak with practice leads or call our Kakkanad office desk at +91 95675 51437.",
      href: "/contact",
      ctaText: "Contact Us →",
    },
  },

  "/pricing": {
    category: "Commercial Pricing Schedules & Modules",
    overviewHref: "/pricing",
    overviewLabel: "Full Pricing Schedule →",
    items: [
      {
        title: "Starter Plan — ₹7,999",
        desc: "Single-page scrollable web presence for micro-businesses & personal portfolios.",
        href: "/pricing#starter",
        badge: "₹7,999",
        icon: "🚀",
      },
      {
        title: "Basic Plan — ₹14,999",
        desc: "Structured 5-page custom static website with image gallery and SSL.",
        href: "/pricing#basic",
        badge: "₹14,999",
        icon: "📄",
      },
      {
        title: "Standard Plan — ₹22,999",
        desc: "8-page static site with micro-animations, rich schema, and FAQ accordions.",
        href: "/pricing#standard",
        badge: "₹22,999",
        icon: "⭐",
      },
      {
        title: "Professional CMS — ₹34,999",
        desc: "CMS-powered dynamic website (WordPress / Strapi) with self-managed blog.",
        href: "/pricing#professional",
        badge: "₹34,999",
        icon: "📝",
      },
      {
        title: "Business E-Commerce — ₹64,999+",
        desc: "Full e-commerce platform with payment gateway (Razorpay/Stripe) and product catalog.",
        href: "/pricing#business",
        badge: "₹64,999+",
        icon: "🛒",
      },
      {
        title: "Custom Web App — ₹99,999+",
        desc: "Bespoke SaaS platforms, custom database architecture, APIs, and cloud CI/CD.",
        href: "/pricing#custom-app",
        badge: "₹99,999+",
        icon: "⚙️",
      },
    ],
    featured: {
      badge: "Custom Proposal",
      title: "Have Specific SLA Needs?",
      desc: "Request an itemized technical scope and dedicated milestone schedule.",
      href: "/contact",
      ctaText: "Request Scope Quote →",
    },
  },

  "/contact": {
    category: "Communication Desks & Inquiries",
    overviewHref: "/contact",
    overviewLabel: "Go to Contact Desk →",
    items: [
      {
        title: "Technical Project Intake",
        desc: "Submit requirements for custom software development, SaaS, or hardware R&D.",
        href: "/contact",
        badge: "Direct Form",
        icon: "📨",
      },
      {
        title: "Helpline & Call Support",
        desc: "Direct telephone assistance with engineering coordinators: +91 95675 51437.",
        href: "tel:+919567551437",
        badge: "+91 95675 51437",
        icon: "📞",
      },
      {
        title: "Registered Office Kakkanad",
        desc: "Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, CSEZ P.O, Kakkanad, Ernakulam.",
        href: "/about#registered-office",
        badge: "Ernakulam",
        icon: "📍",
      },
      {
        title: "Security Vulnerability Intake",
        desc: "Submit responsible vulnerability disclosures with encrypted PGP backup support.",
        href: "/security",
        badge: "Bug Bounty",
        icon: "🛡️",
      },
    ],
    featured: {
      badge: "SLA Commitment",
      title: "24-Hour Engineering SLA",
      desc: "All technical inquiries receive initial architectural assessment within 24 hours.",
      href: "/contact",
      ctaText: "Submit Project Scope →",
    },
  },
};

interface NavOffcanvasProps {
  activePath: string;
  onClose: () => void;
  onItemSelect?: () => void;
}

export function NavOffcanvas({ activePath, onClose, onItemSelect }: NavOffcanvasProps) {
  const data = navOffcanvasMap[activePath];
  if (!data) return null;

  const handleLinkClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (onItemSelect) onItemSelect();
    onClose();
    navigate(href);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -14, scaleY: 0.97 }}
      animate={{ opacity: 1, y: 0, scaleY: 1 }}
      exit={{ opacity: 0, y: -10, scaleY: 0.97 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-full left-0 right-0 z-40 bg-white/98 dark:bg-[#0a121d]/98 backdrop-blur-xl border-b border-slate-200/90 dark:border-slate-800 shadow-2xl shadow-slate-950/20 text-slate-900 dark:text-slate-100 overflow-hidden"
    >
      {/* Top Accent Line with Ambient Glow */}
      <div className="h-0.5 bg-gradient-to-r from-transparent via-brand dark:via-primary to-transparent opacity-80" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activePath}
            initial={{ opacity: 0, y: 8, filter: "blur(3px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -6, filter: "blur(3px)" }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Header Strip */}
            <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100 dark:border-slate-800/80">
              <div className="flex items-center gap-2.5">
                <motion.span
                  initial={{ scale: 0.6 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="w-2 h-2 rounded-full bg-brand dark:bg-primary shadow-[0_0_8px_rgba(57,183,233,0.8)]"
                />
                <h3 className="text-xs font-bold uppercase tracking-wider text-brand-dark dark:text-slate-200">
                  {data.category}
                </h3>
              </div>

              <a
                href={data.overviewHref}
                onClick={(e) => handleLinkClick(data.overviewHref, e)}
                className="text-xs font-bold text-brand dark:text-primary hover:underline transition-colors uppercase tracking-wider inline-flex items-center gap-1 group"
              >
                <span>{data.overviewLabel}</span>
              </a>
            </div>

            {/* Multi-Column Horizontal Layout */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_320px] gap-6 items-start">
              {/* Main Links Grid with Staggered Fluid Fade */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {data.items.map((item, idx) => (
                  <motion.a
                    key={item.title + item.href}
                    href={item.href}
                    onClick={(e) => handleLinkClick(item.href, e)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: idx * 0.03,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    whileHover={{
                      y: -2,
                      scale: 1.012,
                      transition: { duration: 0.18, ease: "easeOut" },
                    }}
                    className="card-interactive block p-3.5 rounded-xl bg-slate-50/80 dark:bg-[#0e1724]/90 border border-slate-200/70 dark:border-slate-800 hover:border-brand/40 dark:hover:border-primary/40 hover:bg-white dark:hover:bg-[#121d2d] transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-bold text-brand-dark dark:text-slate-100 group-hover:text-brand dark:group-hover:text-primary transition-colors flex items-center gap-1.5">
                        {item.icon && <span>{item.icon}</span>}
                        <span>{item.title}</span>
                      </span>
                      {item.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200/60 dark:border-slate-700/60 shrink-0">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 font-light leading-relaxed line-clamp-2">
                      {item.desc}
                    </p>
                  </motion.a>
                ))}
              </div>

              {/* Right Featured Highlight Card */}
              {data.featured && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97, x: 8 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ duration: 0.32, delay: 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="bg-gradient-to-br from-slate-900 to-[#0c1522] text-white p-5 rounded-2xl border border-slate-800 shadow-lg flex flex-col justify-between h-full"
                >
                  <div>
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 uppercase tracking-wider mb-2.5">
                      ★ {data.featured.badge}
                    </span>
                    <h4 className="text-sm font-bold text-white mb-2 leading-snug">
                      {data.featured.title}
                    </h4>
                    <p className="text-xs text-slate-300 font-light leading-relaxed mb-4">
                      {data.featured.desc}
                    </p>
                  </div>

                  <a
                    href={data.featured.href}
                    onClick={(e) => handleLinkClick(data.featured!.href, e)}
                    className="btn-shine w-full text-center py-2.5 px-4 bg-primary hover:bg-sky-400 text-slate-950 rounded-xl font-bold text-xs uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-1.5"
                  >
                    {data.featured.ctaText}
                  </a>
                </motion.div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
